import {
  collectStoryboardPropCandidates,
  findConsistencyAsset,
  getSceneBoundConsistencyAssets,
  type ConsistencyAssetLookups,
} from './consistencyAssets'
import { joinPromptFragments, normalizePromptFragment, normalizePromptList } from './promptUtils'
import type { ConsistencyAssetCollection, StoryboardSceneDraft } from './types'

interface PromptEngineeringContext {
  scene: Record<string, any>
  consistencyAssets: ConsistencyAssetCollection
  consistencyAssetLookups: ConsistencyAssetLookups
  visualStyle?: string
}

function buildStoryboardSubjectPrompt(context: PromptEngineeringContext) {
  const { scene, consistencyAssets, consistencyAssetLookups } = context
  const boundCharacters = getSceneBoundConsistencyAssets(scene, 'character', consistencyAssets)
  const characters = (boundCharacters.length
    ? boundCharacters.map((asset) => joinPromptFragments([asset.name, asset.prompt, asset.notes]))
    : normalizePromptList(scene.characters)
      .map((name) => {
        const asset = findConsistencyAsset(consistencyAssetLookups, 'character', name)
        return asset ? joinPromptFragments([asset.name, asset.prompt, asset.notes]) : name
      }))
    .filter(Boolean)
    .join('、')

  return joinPromptFragments([
    characters,
    scene.action || scene.description,
  ]) || '待补充主体角色与动作表现'
}

function buildStoryboardImageSubjectPrompt(context: PromptEngineeringContext) {
  const { scene, consistencyAssets, consistencyAssetLookups } = context
  const boundCharacters = getSceneBoundConsistencyAssets(scene, 'character', consistencyAssets)
  const characters = (boundCharacters.length
    ? boundCharacters.map((asset) => joinPromptFragments([asset.name, asset.prompt, asset.notes]))
    : normalizePromptList(scene.characters)
      .map((name) => {
        const asset = findConsistencyAsset(consistencyAssetLookups, 'character', name)
        return asset ? joinPromptFragments([asset.name, asset.prompt, asset.notes]) : name
      }))
    .filter(Boolean)
    .join('、')

  return joinPromptFragments([
    characters,
    normalizePromptList(scene.visual_cues).join('、'),
  ]) || '待补充主体角色、服装、表情与当前画面焦点'
}

function buildStoryboardCompositionPrompt(scene: Record<string, any>) {
  return joinPromptFragments([
    scene.shot_type,
    scene.camera_angle,
    scene.camera_movement,
  ]) || '突出主体，保持明确的视觉焦点'
}

function buildStoryboardImageCompositionPrompt(scene: Record<string, any>) {
  return joinPromptFragments([
    scene.shot_type,
    scene.camera_angle,
  ]) || '单帧构图稳定，突出主体，保持明确的视觉焦点'
}

function buildStoryboardSettingPrompt(context: PromptEngineeringContext) {
  const { scene, consistencyAssets, consistencyAssetLookups } = context
  const boundSceneAsset = getSceneBoundConsistencyAssets(scene, 'scene', consistencyAssets)[0] || null
  const sceneAsset = boundSceneAsset || findConsistencyAsset(consistencyAssetLookups, 'scene', scene.location || scene.description)
  const boundProps = getSceneBoundConsistencyAssets(scene, 'prop', consistencyAssets)
  const propPrompt = (boundProps.length
    ? boundProps.map((asset) => joinPromptFragments([asset.name, asset.prompt, asset.notes]))
    : collectStoryboardPropCandidates(scene)
      .map((name) => {
        const asset = findConsistencyAsset(consistencyAssetLookups, 'prop', name)
        return asset ? joinPromptFragments([asset.name, asset.prompt, asset.notes]) : name
      }))
    .filter(Boolean)
    .join('、')

  return joinPromptFragments([
    sceneAsset ? joinPromptFragments([sceneAsset.name, sceneAsset.prompt, sceneAsset.notes]) : scene.location,
    scene.description,
    propPrompt || normalizePromptList(scene.visual_cues).join('、'),
  ]) || '待补充环境、空间关系与关键道具'
}

function buildStoryboardImageSettingPrompt(context: PromptEngineeringContext) {
  const { scene, consistencyAssets, consistencyAssetLookups } = context
  const boundSceneAsset = getSceneBoundConsistencyAssets(scene, 'scene', consistencyAssets)[0] || null
  const sceneAsset = boundSceneAsset || findConsistencyAsset(consistencyAssetLookups, 'scene', scene.location || scene.description)
  const boundProps = getSceneBoundConsistencyAssets(scene, 'prop', consistencyAssets)
  const propPrompt = (boundProps.length
    ? boundProps.map((asset) => joinPromptFragments([asset.name, asset.prompt, asset.notes]))
    : collectStoryboardPropCandidates(scene)
      .map((name) => {
        const asset = findConsistencyAsset(consistencyAssetLookups, 'prop', name)
        return asset ? joinPromptFragments([asset.name, asset.prompt, asset.notes]) : name
      }))
    .filter(Boolean)
    .join('、')

  return joinPromptFragments([
    sceneAsset ? joinPromptFragments([sceneAsset.name, sceneAsset.prompt, sceneAsset.notes]) : scene.location,
    propPrompt,
    normalizePromptList(scene.visual_cues).join('、'),
  ]) || '待补充静态场景、空间层次与关键道具'
}

function buildStoryboardLightingPrompt(scene: Record<string, any>) {
  return joinPromptFragments([
    scene.time_of_day,
    scene.mood,
    scene.lighting_requirements,
  ]) || '结合情绪塑造主光源、轮廓层次与环境氛围'
}

function buildStoryboardActionRhythmPrompt(scene: Record<string, any>) {
  return joinPromptFragments([
    scene.action,
    scene.dialogue_summary,
    scene.mood,
  ]) || '动作清晰，节奏连贯，情绪推进自然'
}

function buildStoryboardConsistencyPrompt(context: PromptEngineeringContext) {
  const { scene, consistencyAssets } = context
  const parts = [
    ...getSceneBoundConsistencyAssets(scene, 'character', consistencyAssets).map((asset) => `角色 ${asset.name} 的五官、发型、服装和身份气质必须与官方参考一致`),
    ...getSceneBoundConsistencyAssets(scene, 'scene', consistencyAssets).map((asset) => `场景 ${asset.name} 的空间结构、陈设和光线氛围必须与官方参考一致`),
    ...getSceneBoundConsistencyAssets(scene, 'prop', consistencyAssets).map((asset) => `道具/产品 ${asset.name} 的造型、材质、颜色和纹样必须与官方参考一致`),
  ]

  return parts.join('；') || '如果画面中出现固定角色或产品，请保持前后一致，不要擅自改脸、改服装或改产品造型'
}

export function buildSceneImageCreativePrompt(context: PromptEngineeringContext) {
  const { scene, visualStyle } = context
  const style = normalizePromptFragment(visualStyle || scene.visual_style) || '统一影视级美术风格，高细节，质感稳定'
  return [
    `画风：${style}`,
    `主体：${buildStoryboardImageSubjectPrompt(context)}`,
    `构图：${buildStoryboardImageCompositionPrompt(scene)}`,
    `场景：${buildStoryboardImageSettingPrompt(context)}`,
    `光影：${buildStoryboardLightingPrompt(scene)}`,
    `一致性要求：${buildStoryboardConsistencyPrompt(context)}`,
    '要求：这是单帧静态图片提示，不要加入镜头运动、节奏变化、转场、连续动作过程或视频分镜说明',
  ].join('\n')
}

export function buildSceneVideoCreativePrompt(context: PromptEngineeringContext) {
  const { scene, visualStyle } = context
  const style = normalizePromptFragment(visualStyle || scene.visual_style) || '统一影视级动态镜头风格，细节稳定，角色一致'
  return [
    `画风：${style}`,
    `主体：${buildStoryboardSubjectPrompt(context)}`,
    `镜头：${buildStoryboardCompositionPrompt(scene)}`,
    `动作节奏：${buildStoryboardActionRhythmPrompt(scene)}`,
    `场景：${buildStoryboardSettingPrompt(context)}`,
    `光影：${buildStoryboardLightingPrompt(scene)}`,
    `一致性要求：${buildStoryboardConsistencyPrompt(context)}`,
  ].join('\n')
}

export function buildScenePromptEngineeringContext(options: {
  scene: StoryboardSceneDraft | Record<string, any>
  consistencyAssets: ConsistencyAssetCollection
  consistencyAssetLookups: ConsistencyAssetLookups
  visualStyle?: string
}): PromptEngineeringContext {
  return {
    scene: options.scene || {},
    consistencyAssets: options.consistencyAssets,
    consistencyAssetLookups: options.consistencyAssetLookups,
    visualStyle: options.visualStyle,
  }
}